import PropTypes from "prop-types"
import React, { useContext } from "react"
import classNames from "classnames"
import {
  faHandHoldingHeart,
  faHandsHelping,
  faLoveseat,
  faUserNurse,
} from "@fortawesome/pro-light-svg-icons"

import { renderIf } from "../../../FeatureToggle/FeatureToggle"
import SingleGeoContext from "../../../../contexts/single_geo"
import StaticImage from "../../../StaticImage"

import {
  careHomesInfoCard,
  container,
  imgContainer,
} from "./styles.module.scss"

import Card from "../../../layout/card/index"
import ListItemWithIcon from "../../../ListItemWithIcon/ListItemWithIcon"
import { BodyText, SubTitle } from "../../../Typography/Typography"
import CareServiceLink from "./CareServiceLink"
import FeaturedServiceTowns from "./FeaturedServiceTowns"

const GeoCareHomesInfoCard = ({
  locationName,
  residentialCareHomeCount = 0,
  nursingCareHomeCount = 0,
  dementiaCareHomeCount = 0,
  palliativeCareHomeCount = 0,
  featuredDementiaTowns,
  featuredNursingTowns,
  featuredPalliativeTowns,
  featuredResidentialTowns,
  isTownPage,
  careServiceType,
  geographicScale,
  districtType,
}) => {
  const RenderIfNotOnDementiaPageAndDementiaCountIsMoreThanZero = renderIf(
    careServiceType !== "dementia" && dementiaCareHomeCount > 0,
  )
  const RenderIfNursingCountIsMoreThanZero = renderIf(nursingCareHomeCount > 0)
  const RenderIfPalliativeCountIsMoreThanZero = renderIf(
    palliativeCareHomeCount > 0,
  )
  const RenderIfResidentialCountIsMoreThanZero = renderIf(
    residentialCareHomeCount > 0,
  )
  const RenderIfTownPage = renderIf(isTownPage)
  const RenderIfNotTownAndHasFeatureDementiaTowns = renderIf(
    !isTownPage && featuredDementiaTowns !== null,
  )
  const RenderIfNotTownAndHasFeaturedNursingTowns = renderIf(
    !isTownPage && featuredNursingTowns !== null,
  )
  const RenderIfNotTownAndHasFeaturedPalliativeTowns = renderIf(
    !isTownPage && featuredPalliativeTowns !== null,
  )
  const RenderIfNotTownAndHasFeaturedResidentialTowns = renderIf(
    !isTownPage && featuredResidentialTowns !== null,
  )

  const title = careServiceType
    ? "Other types of care available"
    : "Types of care available"

  return (
    <Card noPadding noMarginAbove extraClasses={classNames(careHomesInfoCard)}>
      <StaticImage
        name="care_type_default"
        className={classNames(imgContainer)}
      />
      <div className={container}>
        <SubTitle>{title}</SubTitle>
      </div>
      <ul>
        <RenderIfResidentialCountIsMoreThanZero>
          <ListItemWithIcon icon={faLoveseat} iconSize="2x">
            <BodyText bold>Residential care homes</BodyText>
            <BodyText marginBottom>
              Accommodation, meals, and assistance with personal care and
              medication.
            </BodyText>
            <RenderIfTownPage>
              <CareServiceLink
                locationName={locationName}
                serviceType="residential"
              />
            </RenderIfTownPage>
            <RenderIfNotTownAndHasFeaturedResidentialTowns>
              <FeaturedServiceTowns
                careServiceType="residential"
                featuredTowns={featuredResidentialTowns}
              />
            </RenderIfNotTownAndHasFeaturedResidentialTowns>
          </ListItemWithIcon>
        </RenderIfResidentialCountIsMoreThanZero>
        <RenderIfNursingCountIsMoreThanZero>
          <ListItemWithIcon icon={faUserNurse} iconSize="2x" showSeparator>
            <BodyText bold>Nursing care homes</BodyText>
            <BodyText marginBottom>
              Medical care from a qualified nurse, and treatment for residents
              with illness or injuries.
            </BodyText>
            <RenderIfTownPage>
              <CareServiceLink
                locationName={locationName}
                serviceType="nursing"
              />
            </RenderIfTownPage>
            <RenderIfNotTownAndHasFeaturedNursingTowns>
              <FeaturedServiceTowns
                careServiceType="nursing"
                featuredTowns={featuredNursingTowns}
              />
            </RenderIfNotTownAndHasFeaturedNursingTowns>
          </ListItemWithIcon>
        </RenderIfNursingCountIsMoreThanZero>
        <RenderIfNotOnDementiaPageAndDementiaCountIsMoreThanZero>
          <ListItemWithIcon icon={faHandsHelping} iconSize="2x" showSeparator>
            <BodyText bold>Dementia care homes</BodyText>
            <BodyText marginBottom>
              Specialist care designed to meet the challenges faced by people
              living with dementia.
            </BodyText>
            <RenderIfTownPage>
              <CareServiceLink
                locationName={locationName}
                serviceType="dementia"
                geographicScale={geographicScale}
                districtType={districtType}
              />
            </RenderIfTownPage>
            <RenderIfNotTownAndHasFeatureDementiaTowns>
              <FeaturedServiceTowns
                careServiceType="dementia"
                featuredTowns={featuredDementiaTowns}
              />
            </RenderIfNotTownAndHasFeatureDementiaTowns>
          </ListItemWithIcon>
        </RenderIfNotOnDementiaPageAndDementiaCountIsMoreThanZero>
        <RenderIfPalliativeCountIsMoreThanZero>
          <ListItemWithIcon
            icon={faHandHoldingHeart}
            iconSize="2x"
            showSeparator
          >
            <BodyText bold>Palliative and end of life care homes</BodyText>
            <BodyText marginBottom>
              Care designed to provide quality support and comfort for people
              living with life-limited conditions.
            </BodyText>
            <RenderIfTownPage>
              <CareServiceLink
                locationName={locationName}
                serviceType="palliative"
              />
            </RenderIfTownPage>
            <RenderIfNotTownAndHasFeaturedPalliativeTowns>
              <FeaturedServiceTowns
                careServiceType="palliative"
                featuredTowns={featuredPalliativeTowns}
              />
            </RenderIfNotTownAndHasFeaturedPalliativeTowns>
          </ListItemWithIcon>
        </RenderIfPalliativeCountIsMoreThanZero>
      </ul>
    </Card>
  )
}

GeoCareHomesInfoCard.propTypes = {
  locationName: PropTypes.string.isRequired,
  residentialCareHomeCount: PropTypes.number.isRequired,
  nursingCareHomeCount: PropTypes.number.isRequired,
  dementiaCareHomeCount: PropTypes.number.isRequired,
  palliativeCareHomeCount: PropTypes.number.isRequired,
  featuredDementiaTowns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      ratedPositiveCount: PropTypes.number,
    }),
  ),
  featuredNursingTowns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      ratedPositiveCount: PropTypes.number,
    }),
  ),
  featuredPalliativeTowns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      ratedPositiveCount: PropTypes.number,
    }),
  ),
  featuredResidentialTowns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      ratedPositiveCount: PropTypes.number,
    }),
  ),
  isTownPage: PropTypes.bool.isRequired,
  careServiceType: PropTypes.oneOf(["dementia"]),
  geographicScale: PropTypes.string.isRequired,
  districtType: PropTypes.string,
}

GeoCareHomesInfoCard.defaultProps = {
  featuredDementiaTowns: null,
  featuredNursingTowns: null,
  featuredPalliativeTowns: null,
  featuredResidentialTowns: null,
  careServiceType: undefined,
  districtType: undefined,
}

export const ConnectedGeoCareHomesInfoCard = ({
  isTownPage,
  careServiceType,
}) => {
  const geoContext = useContext(SingleGeoContext)

  const {
    name,
    dementiaCareHomeCount,
    nursingCareHomeCount,
    palliativeCareHomeCount,
    residentialCareHomeCount,
    featuredDementiaTowns,
    featuredNursingTowns,
    featuredPalliativeTowns,
    featuredResidentialTowns,
    geographicScale,
    districtType,
  } = geoContext

  return (
    <GeoCareHomesInfoCard
      locationName={name}
      residentialCareHomeCount={residentialCareHomeCount}
      nursingCareHomeCount={nursingCareHomeCount}
      dementiaCareHomeCount={dementiaCareHomeCount}
      palliativeCareHomeCount={palliativeCareHomeCount}
      featuredDementiaTowns={featuredDementiaTowns}
      featuredNursingTowns={featuredNursingTowns}
      featuredPalliativeTowns={featuredPalliativeTowns}
      featuredResidentialTowns={featuredResidentialTowns}
      isTownPage={isTownPage}
      careServiceType={careServiceType}
      geographicScale={geographicScale}
      districtType={districtType}
    />
  )
}

ConnectedGeoCareHomesInfoCard.propTypes = {
  isTownPage: PropTypes.bool.isRequired,
  careServiceType: PropTypes.oneOf(["dementia"]),
}

ConnectedGeoCareHomesInfoCard.defaultProps = {
  careServiceType: undefined,
}

export default GeoCareHomesInfoCard
